/**
 * 환경설정 &gt; 스팸 (migration 160, 2026-09-05 사용자 지시).
 *
 * 절 셋 — 스팸 기본설정 · 수신허용목록 · 수신거부목록.
 *
 * ★★★ **저장바가 없다.** 고른 순간 반영된다(정본 P2-1 「반영 시점의 단위는 탭」).
 *    까닭: 정책을 바꾸면 아래 두 목록이 **보였다 사라진다**. 저장을 눌러야 반영되는 화면이라면
 *    감춘 목록이 아직 그대로 도는 중이라, 화면은 「없다」고 말하는데 서버는 여전히 막는다.
 * ★★★ **고른 값이 다른 칸을 뜻 없게 만들면 감춘다**(정본 P2-1). 잠그지 않는다 —
 *    잠근 목록은 「내가 적어 둔 주소가 저기 있는데 손을 못 댄다」로 읽히지만,
 *    사실은 **그 목록이 아무 일도 하지 않는다.**
 *      · 모든메일 수신            → 두 목록 다 감춘다(아무것도 안 거른다)
 *      · 모든메일 차단 · 허용만   → 수신허용목록만 (거부목록은 이미 전부 막혀 뜻이 없다)
 *      · 자동 사용함 · 사용안함   → 둘 다 (사용안함은 「자동 판정만 끈 것」이다)
 * ★★ 실패하면 **고른 것을 되돌린다.** 화면에는 새 값이 있는데 서버에는 옛 값이 남는 것이
 *    가장 나쁘다 — 「분명히 바꿨는데 왜 그대로냐」가 된다.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  clearMailAddressRules, createMailAddressRule, deleteMailAddressRule, listMailAddressRules,
  type MailAddressRule, type MailAddressRuleKind,
} from '../../api/mail';
import {
  getMySettings, patchMySettings,
  type MailSpamAction, type MailSpamLevel, type MailSpamPolicy,
} from '../../api/users';
import { Plus, Trash2 } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Select } from '../../components/shared/Select';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailAddressRuleModal } from './MailAddressRuleModal';
import { MailConfirmDialog } from './MailConfirmDialog';

/** 서버 기본값과 **같은 값**이다(migration 160 DEFAULT · mail-spam-policy.ts SPAM_DEFAULTS). */
const DEFAULTS = { policy: 'AUTO' as MailSpamPolicy, level: 'HIGH' as MailSpamLevel, action: 'SPAM_BOX' as MailSpamAction };

const POLICY_OPTIONS: { value: MailSpamPolicy; labelKey: MailI18nKey }[] = [
  { value: 'AUTO', labelKey: 'mail.spamset.p.auto' },
  { value: 'OFF', labelKey: 'mail.spamset.p.off' },
  { value: 'ALL_RECEIVE', labelKey: 'mail.spamset.p.all_receive' },
  { value: 'ALL_BLOCK', labelKey: 'mail.spamset.p.all_block' },
  { value: 'ALLOW_ONLY', labelKey: 'mail.spamset.p.allow_only' },
];

const LEVEL_OPTIONS: { value: MailSpamLevel; labelKey: MailI18nKey; hintKey: MailI18nKey }[] = [
  { value: 'HIGH', labelKey: 'mail.spamset.l.high', hintKey: 'mail.spamset.lh.high' },
  { value: 'MEDIUM', labelKey: 'mail.spamset.l.medium', hintKey: 'mail.spamset.lh.medium' },
  { value: 'LOW', labelKey: 'mail.spamset.l.low', hintKey: 'mail.spamset.lh.low' },
];

const ACTION_OPTIONS: { value: MailSpamAction; labelKey: MailI18nKey; hintKey?: MailI18nKey }[] = [
  { value: 'SPAM_BOX', labelKey: 'mail.spamset.a.spam_box', hintKey: 'mail.spamset.ah.spam_box' },
  { value: 'TRASH', labelKey: 'mail.spamset.a.trash' },
  { value: 'DELETE', labelKey: 'mail.spamset.a.delete', hintKey: 'mail.spamset.ah.delete' },
];

/** 서버가 준 코드 → 사람 말. 모르는 코드는 뭉뚱그린 실패로 적는다. */
function messageFor(err: unknown, fallback: MailI18nKey): string {
  const res = (err as { response?: { status?: number; data?: { code?: string } } }).response;
  switch (res?.data?.code) {
    case 'INVALID_ADDRESS': return tMail('mail.spamset.err_addr');
    case 'RULE_EXISTS': return tMail('mail.spamset.err_exists');
    case 'RULE_CONFLICT': return tMail('mail.spamset.err_conflict');
    case 'SELF_ADDRESS': return tMail('mail.spamset.err_self');
    default: return tMail(fallback);
  }
}

export function MailSettingsSpam() {
  const [spam, setSpam] = useState(DEFAULTS);
  const [allow, setAllow] = useState<MailAddressRule[]>([]);
  const [block, setBlock] = useState<MailAddressRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [addFor, setAddFor] = useState<MailAddressRuleKind | null>(null);
  const [clearFor, setClearFor] = useState<MailAddressRuleKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  const uid = useId();
  const id = (k: string) => `${uid}-${k}`;

  const loadRules = useCallback(async () => {
    const rules = await listMailAddressRules();
    setAllow(rules.allow);
    setBlock(rules.block);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getMySettings(), listMailAddressRules()])
      .then(([s, rules]) => {
        if (cancelled) return;
        setSpam({
          policy: s.mailSpamPolicy ?? DEFAULTS.policy,
          level: s.mailSpamLevel ?? DEFAULTS.level,
          action: s.mailSpamAction ?? DEFAULTS.action,
        });
        setAllow(rules.allow);
        setBlock(rules.block);
      })
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.spamset.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /** 고른 순간 저장한다 — 실패하면 화면을 **원래 값으로 되돌린다**. */
  const save = useCallback(async (patch: Partial<typeof DEFAULTS>) => {
    const before = spam;
    const next = { ...spam, ...patch };
    setSpam(next);
    setNote(null);
    try {
      await patchMySettings({
        ...(patch.policy ? { mailSpamPolicy: patch.policy } : {}),
        ...(patch.level ? { mailSpamLevel: patch.level } : {}),
        ...(patch.action ? { mailSpamAction: patch.action } : {}),
      });
      setNote({ ok: true, text: tMail('mail.spamset.saved') });
    } catch (err) {
      setSpam(before);
      // 이 키를 모르는 서버는 PATCH 스키마가 닫혀 있어 400 을 낸다 — 사실만 알린다.
      const status = (err as { response?: { status?: number } }).response?.status;
      setNote({ ok: false, text: tMail(status === 400 ? 'mail.spamset.legacy' : 'mail.spamset.save_failed') });
    }
  }, [spam]);

  const add = useCallback(async (kind: MailAddressRuleKind, address: string) => {
    setBusy(true);
    setModalErr(null);
    try {
      await createMailAddressRule(kind, address);
      await loadRules();
      setAddFor(null);
      setNote({ ok: true, text: tMail('mail.spamset.saved') });
    } catch (err) {
      setModalErr(messageFor(err, 'mail.spamset.err_failed'));
    } finally {
      setBusy(false);
    }
  }, [loadRules]);

  const remove = useCallback(async (ruleId: string) => {
    setBusy(true);
    setNote(null);
    try {
      await deleteMailAddressRule(ruleId);
      await loadRules();
      setNote({ ok: true, text: tMail('mail.spamset.saved') });
    } catch (err) {
      setNote({ ok: false, text: messageFor(err, 'mail.spamset.del_failed') });
    } finally {
      setBusy(false);
    }
  }, [loadRules]);

  const clearAll = useCallback(async (kind: MailAddressRuleKind) => {
    setBusy(true);
    setNote(null);
    try {
      await clearMailAddressRules(kind);
      await loadRules();
      setClearFor(null);
      setNote({ ok: true, text: tMail('mail.spamset.saved') });
    } catch (err) {
      setNote({ ok: false, text: messageFor(err, 'mail.spamset.del_failed') });
    } finally {
      setBusy(false);
    }
  }, [loadRules]);

  const columns: ReadonlyArray<TableColumn<MailAddressRule>> = useMemo(() => [
    {
      key: 'address',
      header: tMail('mail.spamset.col.address'),
      rowHeader: true,
      render: (r) => (
        <span className="spamrule__addr">
          <span className="spamrule__mail">{r.address}</span>
          {/* 상세에서 「이 사람 메일도 차단」을 눌러 생긴 줄 — 어디서 왔는지 적어야
              「내가 적은 적 없는 주소가 왜 여기 있나」가 안 된다. */}
          {r.source === 'SENDER' && <span className="spamrule__src">{tMail('mail.spamset.from_report')}</span>}
        </span>
      ),
    },
    {
      key: 'manage',
      header: tMail('mail.spamset.col.manage'),
      width: '96px',
      render: (r) => (
        <Button
          variant="quiet-danger"
          size="sm"
          disabled={busy}
          iconLeft={<Trash2 size={13} aria-hidden />}
          aria-label={tMail('mail.spamset.delete_aria').replace('{n}', r.address)}
          onClick={() => { void remove(r.id); }}
        >
          {tMail('mail.spamset.delete')}
        </Button>
      ),
    },
  ], [busy, remove]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  // 고른 값이 다른 칸을 뜻 없게 만들면 감춘다(정본 P2-1) — 위 주석의 표 그대로.
  const showAllow = spam.policy !== 'ALL_RECEIVE';
  const showBlock = spam.policy === 'AUTO' || spam.policy === 'OFF';

  const listSection = (kind: MailAddressRuleKind) => {
    const rows = kind === 'ALLOW' ? allow : block;
    const headingKey: MailI18nKey = kind === 'ALLOW' ? 'mail.spamset.allow' : 'mail.spamset.block';
    const noneKey: MailI18nKey = kind === 'ALLOW' ? 'mail.spamset.allow_none' : 'mail.spamset.block_none';
    return (
      <section className="wc-set__sec" aria-labelledby={id(kind)}>
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id={id(kind)}>{tMail(headingKey)}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setAddFor(kind); }}
          >
            {tMail('mail.spamset.add')}
          </Button>
          {/* 지울 것이 없으면 잠긴다 — 눌러도 아무 일이 없는 단추를 세우지 않는다(정본 P2-1). */}
          <Button
            variant="quiet-danger" size="sm"
            disabled={rows.length === 0 || busy}
            onClick={() => setClearFor(kind)}
          >
            {tMail('mail.spamset.clear')}
          </Button>
        </div>

        <Table
          caption={tMail(headingKey)}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          empty={<span>{tMail(noneKey)}</span>}
          wrapClassName="mbox__tablewrap"
        />
        {/* 빈 목록은 표 안에서 「무엇을 하는 목록인가」를 말한다 — 곁말은 줄이 있을 때만. */}
        {rows.length > 0 && (
          <p className="wc-set__hint">
            {tMail(kind === 'ALLOW' ? 'mail.spamset.allow_hint' : 'mail.spamset.block_hint')}
          </p>
        )}
      </section>
    );
  };

  const levelHint = LEVEL_OPTIONS.find((o) => o.value === spam.level)?.hintKey;
  const actionHint = ACTION_OPTIONS.find((o) => o.value === spam.action)?.hintKey;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby={id('basic')}>
        <h2 className="wc-set__sech" id={id('basic')}>{tMail('mail.spamset.sec.basic')}</h2>
        <div className="wc-set__rows">

          <div className="wc-set__row">
            <span className="wc-set__label">{tMail('mail.spamset.policy')}</span>
            <div className="wc-set__ctl">
              <Select
                className="wc-set__w3"
                ariaLabel={tMail('mail.spamset.policy')}
                value={spam.policy}
                onChange={(v) => { void save({ policy: v as MailSpamPolicy }); }}
                options={POLICY_OPTIONS.map((o) => ({ value: o.value, label: tMail(o.labelKey) }))}
              />
              <p className="wc-set__hint">{tMail('mail.spamset.scope_hint')}</p>
            </div>
          </div>

          {/* 값에 딸린 줄 — 「자동스팸차단 사용함」일 때만 뜻이 있다(정본 P2-1). */}
          {spam.policy === 'AUTO' && (
            <div className="wc-set__row wc-set__row--sub">
              <span className="wc-set__label">{tMail('mail.spamset.level')}</span>
              <div className="wc-set__ctl">
                <Select
                  className="wc-set__w3"
                  ariaLabel={tMail('mail.spamset.level')}
                  value={spam.level}
                  onChange={(v) => { void save({ level: v as MailSpamLevel }); }}
                  options={LEVEL_OPTIONS.map((o) => ({ value: o.value, label: tMail(o.labelKey) }))}
                />
                {levelHint && <p className="wc-set__hint">{tMail(levelHint)}</p>}
              </div>
            </div>
          )}

          <div className="wc-set__row">
            <span className="wc-set__label">{tMail('mail.spamset.action')}</span>
            <div className="wc-set__ctl">
              <Select
                className="wc-set__w3"
                ariaLabel={tMail('mail.spamset.action')}
                value={spam.action}
                onChange={(v) => { void save({ action: v as MailSpamAction }); }}
                options={ACTION_OPTIONS.map((o) => ({ value: o.value, label: tMail(o.labelKey) }))}
              />
              {actionHint && <p className="wc-set__hint">{tMail(actionHint)}</p>}
            </div>
          </div>

          {/* 저장바가 없는 탭이라 「됐다」를 여기서 말한다(정본 P2 · P2-1). */}
          <div className="wc-set__row">
            <span className="wc-set__label" aria-hidden />
            <p className={note?.ok === false ? 'wc-set__bad' : 'wc-set__ok'} role="status">
              {note?.text ?? ''}
            </p>
          </div>
        </div>
      </section>

      {showAllow && listSection('ALLOW')}
      {showBlock && listSection('BLOCK')}

      {addFor && (
        <MailAddressRuleModal
          kind={addFor}
          busy={busy}
          error={modalErr}
          onClose={() => { setAddFor(null); setModalErr(null); }}
          onSubmit={(address) => { void add(addFor, address); }}
        />
      )}

      {/* 되돌릴 수 없는 일은 반드시 한 번 묻는다 — 목록·상세가 쓰던 그 창이다. */}
      <MailConfirmDialog
        open={clearFor !== null}
        danger
        busy={busy}
        title={clearFor === 'ALLOW'
          ? tMail('mail.spamset.clear_allow_title')
          : tMail('mail.spamset.clear_block_title')}
        description={clearFor === 'ALLOW'
          ? tMail('mail.spamset.clear_allow_desc')
          : tMail('mail.spamset.clear_block_desc')}
        confirmLabel={tMail('mail.spamset.clear')}
        onCancel={() => setClearFor(null)}
        onConfirm={() => { if (clearFor) void clearAll(clearFor); }}
      />
    </div>
  );
}
